//services/api.js
import axios from "axios";

const API_URL = "https://app.rental.itup.com.br/rental_auth/api";
// http://localhost:3003
//https://app.rental.itup.com.br/rental_auth
export const api = axios.create({
	baseURL: API_URL,
	headers: { "Content-Type": "application/json" },
});

// Add an interceptor to catch errors globally and call the global error handler
api.interceptors.response.use(
	(response) => response,
	(error) => {
		const errorMessage = error.response?.data?.error || "An unexpected error occurred.";
		// Use the global error handler if available
		if (window.showGlobalError) {
			window.showGlobalError(errorMessage);
		}
		return Promise.reject(error);
	}
);
/**
 * Authenticates a user.
 *
 * Endpoint: POST /login
 * (Mounted on /api if using API_URL with /api base)
 *
 * Request Body:
 *   - username (string, required)
 *   - password (string, required)
 *
 * Response:
 *   - { token: string }
 *
 * Errors:
 *   - 400: Missing username/password or invalid credentials.
 *   - 500: Internal server error.
 */
export const login = async (username, password) => {
	const response = await api.post("/login", { username, password });
	return response.data;
};

/**
 * Registers a new user.
 *
 * Endpoint: POST /register
 *
 * Request Body:
 *   - username (string, required)
 *   - password (string, required)
 *
 * Response:
 *   - { message: string, userId: number }
 *
 * Errors:
 *   - 400: Missing username or password.
 *   - 500: Internal server error.
 */
export const register = async (username, password) => {
	const response = await api.post("/register", { username, password });
	return response.data;
};

/**
 * Retrieves all Enviroments.
 *
 * Endpoint: GET /items
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * Response:
 *   - Array of items from the "wizardeasymobile" table.
 *
 * Errors:
 *   - 500: Internal server error.
 */
export const getItems = async (token) => {
	const response = await api.get("/items", { headers: { Authorization: `Bearer ${token}` } });
	return response.data;
};

/**
 * Retrieves Enviroments accessible to a customer user.
 *
 * Endpoint: GET /items/customer
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * Process:
 *   1. Identifies the company linked to the current user.
 *   2. Retrieves environments assigned to that company.
 *   3. Returns items matching those environments.
 *
 * Response:
 *   - { companyId, companyName, environments: Array, items: Array }
 *
 * Errors:
 *   - 400: User ID missing (if not found in token).
 *   - 500: Internal server error.
 */
export const getItemsCustomer = async (token) => {
	const response = await api.get("/items/customer", { headers: { Authorization: `Bearer ${token}` } });
	return response.data;
};

/**
 * Adds a new Enviroment.
 *
 * Endpoint: POST /items
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * Request Body (JSON):
 *   - Environment, AliasAccess, Enable, CnpjCli, arquivojs, NomeCli, Descricao,
 *     UrlRestProtheus, UrlRestProtheus2, UrlRedirectEasy, UrlJsCustomCliente,
 *     UrlCssCustomCliente, GeraLog, AvisaLog, Enableauth, UserName, Password,
 *     DateLimit, wizardeasymobilecol
 *   - Note: NomeCli, Descricao, and UrlRestProtheus are required.
 *
 * Response:
 *   - { message: "Item added successfully", itemId: number }
 *
 * Errors:
 *   - 422: Missing required fields.
 *   - 500: Internal server error.
 */
export const addItem = async (token, item) => {
	const response = await api.post("/items", item, { headers: { Authorization: `Bearer ${token}` } });
	return response.data;
};

/**
 * Updates an existing Enviroment.
 *
 * Endpoint: PUT /items/:alias
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * URL Parameter:
 *   - alias (string): Identifier of the item to update.
 *
 * Request Body:
 *   - Partial or complete set of fields to update.
 *
 * Response:
 *   - { message: "Item updated successfully" }
 *
 * Errors:
 *   - 400: No fields provided.
 *   - 404: Item not found.
 *   - 500: Internal server error.
 */
export const updateItem = async (token, id, item) => {
	const response = await api.put(`/items/${id}`, item, { headers: { Authorization: `Bearer ${token}` } });
	return response.data;
};

/**
 * Deletes an Enviroment.
 *
 * Endpoint: DELETE /items/:alias
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * URL Parameter:
 *   - alias (string): Identifier of the item to delete.
 *
 * Response:
 *   - { message: "Item deleted successfully" }
 *
 * Errors:
 *   - 404: Item not found.
 *   - 500: Internal server error.
 */
export const deleteItem = async (token, alias) => {
	const response = await api.delete(`/items/${alias}`, { headers: { Authorization: `Bearer ${token}` } });
	return response.data;
};

/**
 * Retrieves all users.
 *
 * Endpoint: GET /users
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * Response:
 *   - Array of user objects with properties: id, username, role, is_enabled, created_at.
 *
 * Errors:
 *   - 500: Error fetching users.
 */
export const getUsers = async (token) => {
	return fetch(`${API_URL}/users`, {
		headers: { Authorization: `Bearer ${token}` },
	}).then((res) => res.json());
};

/**
 * Updates a user.
 *
 * Endpoint: PUT /users/:id
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * URL Parameter:
 *   - id (number): User identifier.
 *
 * Request Body:
 *   - username (string)
 *   - role (string)
 *   - email
 *   - nome_completo
 *   - is_enabled (boolean/number)
 *
 * Response:
 *   - { message: "Usuário atualizado com sucesso" }
 *
 * Errors:
 *   - 404: User not found.
 *   - 500: Error updating user.
 */
export const updateUser = async (token, id, user) => {
	return fetch(`${API_URL}/users/${id}`, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify(user),
	}).then((res) => res.json());
};

/**
 * Deletes a user.
 *
 * Endpoint: DELETE /users/:id
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * URL Parameter:
 *   - id (number): User identifier.
 *
 * Response:
 *   - { message: "Usuário excluído com sucesso" }
 *
 * Errors:
 *   - 404: User not found.
 *   - 500: Error deleting user.
 */
export const deleteUser = async (token, id) => {
	return fetch(`${API_URL}/users/${id}`, {
		method: "DELETE",
		headers: { Authorization: `Bearer ${token}` },
	}).then((res) => res.json());
};

/**
 * Retrieves a list of companies.
 *
 * Endpoint: GET /companies
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * Response:
 *   - Array of company objects.
 *
 * Errors:
 *   - 500: Error fetching companies.
 */
export const getCompanies = async (token) => {
	const response = await api.get("/companies", { headers: { Authorization: `Bearer ${token}` } });
	return response.data;
};

/**
 * Creates a new company.
 *
 * Endpoint: POST /companies
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * Request Body:
 *   - name (string, required)
 *   - cnpj (string, required)
 *
 * Response:
 *   - { message: "Company created successfully", companyId: number }
 *
 * Errors:
 *   - 400: Missing name or CNPJ.
 *   - 500: Error creating company.
 */
export const addCompany = async (token, company) => {
	const response = await api.post("/companies", company, { headers: { Authorization: `Bearer ${token}` } });
	return response.data;
};

/**
 * Retrieves users assigned to a company (passing companyId).
 *
 * Endpoint: GET /companies/:companyId/users
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * URL Parameter:
 *   - companyId (number): Company identifier.
 *
 * Response:
 *   - Array of users (id, username, role).
 *
 * Errors:
 *   - 500: Error fetching company users.
 */
export const getCompanyUsers = async (token, companyId) => {
	const response = await api.get(`/companies/${companyId}/users`, { headers: { Authorization: `Bearer ${token}` } });
	return response.data;
};

/**
 * Retrieves the company associated with a user (passing userId).
 *
 * Endpoint: GET /companies/:userId/company
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * URL Parameter:
 *   - userId (number): User identifier.
 *
 * Response:
 *   - { company_id: number|null, company_name: string }
 *     (If the user is not assigned, returns { company_id: null, company_name: "Não atribuído" }.)
 *
 * Errors:
 *   - 500: Error fetching user's company.
 */
export const getUserCompany = async (token, userId) => {
	const response = await api.get(`/companies/${userId}/company`, {
		headers: { Authorization: `Bearer ${token}` },
	});
	console.log(userId);
	return response.data;
};

/**
 * Assigns a user to a company (or updates the assignment if already exists).
 *
 * Endpoint: POST /companies/assign-user
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * Request Body:
 *   - companyId (number, required)
 *   - userId (number, required)
 *   - role (string, required)
 *
 * Response:
 *   - { message: "User assigned to company successfully", companyUserId?: number }
 *
 * Errors:
 *   - 400: Missing required fields.
 *   - 500: Error assigning user to company.
 */
export const assignUserToCompany = async (token, companyUser) => {
	const response = await api.post(`/companies/assign-user`, companyUser, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

/**
 * Retrieves customer (Admin of company) for a specific company.
 *
 * Endpoint: GET /customer-users/:companyId/users
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * URL Parameter:
 *   - companyId (number): Company identifier.
 *
 * Response:
 *   - Array of customer user objects (id, username, password_hash, role, is_enabled, created_at).
 *
 * Errors:
 *   - 500: Error fetching customer users.
 */
export const getCustomerUsers = async (token, companyId) => {
	const response = await api.get(`/customer-users/${companyId}/users`, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

/**
 * Creates a new customer (Admin of company) for a company.
 *
 * Endpoint: POST /customer-users
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * Request Body:
 *   - companyId (number, required)
 *   - username (string, required)
 *   - password (string, required)
 *   - role (string, required)
 *
 * Response:
 *   - { message: "Customer user created successfully", userId: number }
 *
 * Errors:
 *   - 400: Missing required fields.
 *   - 500: Error creating customer user.
 */
export const addCustomerUser = async (token, customerUser) => {
	const response = await api.post("/customer-users", customerUser, { headers: { Authorization: `Bearer ${token}` } });
	return response.data;
};

/**
 * Updates an existing customer (Company adm).
 *
 * Endpoint: PUT /customer-users/:id
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * URL Parameter:
 *   - id (number): Customer user identifier.
 *
 * Request Body:
 *   - username (string)
 *   - password (string, optional; if provided, will update password)
 *   - role (string)
 *   - is_enabled (boolean/number)
 *
 * Response:
 *   - { message: "Customer user updated successfully" }
 *
 * Errors:
 *   - 404: Customer user not found.
 *   - 500: Error updating customer user.
 */
export const updateCustomerUser = async (token, userId, customerUser) => {
	const response = await api.put(`/customer-users/${userId}`, customerUser, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

/**
 * Disable a customer user (Company Adm).
 *
 * Endpoint: PUT /customer-users/:id
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * URL Parameter:
 *   - id (number): Customer user identifier.
 *
 * Response:
 *   - { message: "Customer user disabled  successfully" }
 *
 * Errors:
 *   - 404: Customer user not found.
 *   - 500: Error disabling customer user.
 */
export const disableCustomerUser = async (token, userId) => {
	const response = await api.put(
		`/customer-users/${userId}/disable`,
		{}, // Empty body
		{ headers: { Authorization: `Bearer ${token}` } } // Headers go here
	);
	return response.data;
};

/**
 * Assigns an environment to a company.
 *
 * Endpoint: POST /companies/assign-environment
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * Request Body:
 *   - companyId (number, required)
 *   - environmentId (string/number, required)
 *
 * Response:
 *   - { message: "Environment assigned successfully", id: number }
 *
 * Errors:
 *   - 400: Missing required fields.
 *   - 500: Error linking environment to company.
 */
export const assignEnvironmentToCompany = async (token, companyId, environmentId) => {
	const response = await api.post(
		"/companies/assign-environment",
		{ companyId, environmentId },
		{ headers: { Authorization: `Bearer ${token}` } }
	);
	return response.data;
};

/**
 * Retrieves environments linked to a company.
 *
 * Endpoint: GET /companies/:companyId/environments
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * URL Parameter:
 *   - companyId (number): Company identifier.
 *
 * Response:
 *   - Array of environment objects (e.g. AliasAccess, NomeCli, UrlRestProtheus).
 *
 * Errors:
 *   - 500: Error fetching company environments.
 */
export const getCompanyEnvironments = async (token, companyId) => {
	const response = await api.get(`/companies/${companyId}/environments`, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

/**
 * Removes an environment assignment from a company.
 *
 * Endpoint: DELETE /companies/remove-environment
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * Request Data (sent in the body):
 *   - companyId (number, required)
 *   - environmentId (string/number, required)
 *
 * Response:
 *   - { message: "Environment unlinked successfully" }
 *
 * Errors:
 *   - 400: Missing required fields.
 *   - 404: Environment not found for company.
 *   - 500: Error unlinking environment from company.
 */
export const removeEnvironmentFromCompany = async (token, companyId, environmentId) => {
	const response = await api.delete(`/companies/${companyId}/environments/${environmentId}`, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

/**
 * Toggles the status of a company (enabled/disabled).
 *
 * Endpoint: PUT /companies/:id/toggle-status
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * URL Parameter:
 *   - id (number): Company identifier.
 *
 * Response:
 *   - { message: "Company enabled successfully" } or { message: "Company disabled successfully" }
 *
 * Errors:
 *   - 404: Company not found.
 *   - 500: Error updating company status.
 */
export const toggleCompanyStatus = async (token, companyId) => {
	const response = await api.put(
		`/companies/${companyId}/toggle-status`,
		{},
		{
			headers: { Authorization: `Bearer ${token}` },
		}
	);
	return response.data;
};

// Bring all enviroments that are not linked to companies
export const getUnlinkedEnvironments = async (token) => {
	const response = await api.get("/companies/unlinked-environments", {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

/**
 * Retrieves the list of environment users for a specific environment.
 *
 * @param {string} token - The authentication token (JWT).
 * @param {string} environmentId - The identifier of the environment.
 * @returns {Promise<any>} A promise that resolves to the data returned by the API.
 */
export const getEnvironmentUsers = async (token, environmentId) => {
	const response = await api.get(`/environment-users/${environmentId}/users`, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

/**
 * Adds a new user to the specified environment.
 *
 * @param {string} token - The authentication token (JWT).
 * @param {Object} user - The user object containing the necessary details (e.g., username, password, role, etc.).
 * @returns {Promise<any>} A promise that resolves to the data returned by the API.
 */
export const addEnvironmentUser = async (token, user) => {
	const response = await api.post("/environment-users", user, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

/**
 * Updates an existing environment user's information.
 *
 * @param {string} token - The authentication token (JWT).
 * @param {number|string} id - The identifier of the environment user to update.
 * @param {Object} user - The updated user object containing the fields to be modified.
 * @returns {Promise<any>} A promise that resolves to the data returned by the API.
 */
export const updateEnvironmentUser = async (token, id, user) => {
	const response = await api.put(`/environment-users/${id}`, user, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

/**
 * Toggles the active status of an environment user (activates or deactivates the user).
 *
 * @param {string} token - The authentication token (JWT).
 * @param {number|string} id - The identifier of the environment user whose status is to be toggled.
 * @returns {Promise<any>} A promise that resolves to the data returned by the API.
 */
export const toggleEnvironmentUser = async (token, id) => {
	const response = await api.put(
		`/environment-users/${id}/toggle`,
		{},
		{
			headers: { Authorization: `Bearer ${token}` },
		}
	);
	return response.data;
};

//Return some details of environment, but the main role is to bring max_users
export const getCompanyEnvironmentDetails = async (token, companyId) => {
	const response = await api.get(`/companies/${companyId}/environments/details`, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

export const updateEnvironmentUserLimit = async (token, environmentId, maxUsers) => {
	const response = await api.put(
		`/environment-users/${environmentId}/user-limit`,
		{ max_users: maxUsers },
		{
			headers: {
				Authorization: token,
			},
		}
	);
	return response.data;
};

/**
 * Obtém todos os apps.
 *
 * Endpoint: GET /apps
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * Response:
 *   - Array de objetos { id, app_name, description, is_enabled, created_at }
 *
 * Errors:
 *   - 500: Erro interno.
 */
export const getApps = async (token) => {
	const response = await api.get('/apps', {
	  headers: { Authorization: `Bearer ${token}` }
	});
	return response.data;
  };
  
  /**
   * Obtém um app pelo ID.
   *
   * Endpoint: GET /apps/:id
   *
   * Headers:
   *   - Authorization: Bearer <token>
   *
   * URL Parameter:
   *   - id (number): Identificador do app.
   *
   * Response:
   *   - Objeto { id, app_name, description, is_enabled, created_at }
   *
   * Errors:
   *   - 404: App não encontrado.
   *   - 500: Erro interno.
   */
  export const getAppById = async (token, id) => {
	const response = await api.get(`/apps/${id}`, {
	  headers: { Authorization: `Bearer ${token}` }
	});
	return response.data;
  };
  
  /**
   * Cria um novo app.
   *
   * Endpoint: POST /apps
   *
   * Headers:
   *   - Authorization: Bearer <token>
   *
   * Request Body:
   *   - app_name (string, obrigatório)
   *   - description (string, opcional)
   *   - is_enabled (boolean|number, opcional)
   *
   * Response:
   *   - { message: "App criado", appId: number }
   *
   * Errors:
   *   - 400: app_name faltando.
   *   - 500: Erro interno.
   */
  export const addApp = async (token, app) => {
	const response = await api.post('/apps', app, {
	  headers: { Authorization: `Bearer ${token}` }
	});
	return response.data;
  };
  
  /**
   * Atualiza um app existente.
   *
   * Endpoint: PUT /apps/:id
   *
   * Headers:
   *   - Authorization: Bearer <token>
   *
   * URL Parameter:
   *   - id (number): Identificador do app.
   *
   * Request Body:
   *   - app_name, description, is_enabled (qualquer combinação)
   *
   * Response:
   *   - { message: "App atualizado" }
   *
   * Errors:
   *   - 404: App não encontrado.
   *   - 500: Erro interno.
   */
  export const updateApp = async (token, id, app) => {
	const response = await api.put(`/apps/${id}`, app, {
	  headers: { Authorization: `Bearer ${token}` }
	});
	return response.data;
  };
  
  /**
   * Desabilita (delete “soft”) um app.
   *
   * Endpoint: DELETE /apps/:id
   *
   * Headers:
   *   - Authorization: Bearer <token>
   *
   * URL Parameter:
   *   - id (number): Identificador do app.
   *
   * Response:
   *   - { message: "App desabilitado" }
   *
   * Errors:
   *   - 404: App não encontrado.
   *   - 500: Erro interno.
   */
  export const deleteApp = async (token, id) => {
	const response = await api.delete(`/apps/${id}`, {
	  headers: { Authorization: `Bearer ${token}` }
	});
	return response.data;
  };
  
  /**
   * Obtém todos os vínculos app–company, opcionalmente filtrando por companyId.
   *
   * Endpoint: GET /company-apps[?companyId=]
   *
   * Headers:
   *   - Authorization: Bearer <token>
   *
   * Query Parameter:
   *   - companyId (number, opcional): filtra apenas pelo ID da company.
   *
   * Response:
   *   - Array de objetos { id, company_id, app_id, app_name, max_users, created_at }
   *
   * Errors:
   *   - 500: Erro interno.
   */
  export const getCompanyApps = async (token, companyId) => {
	const response = await api.get('/company-apps', {
	  headers: { Authorization: `Bearer ${token}` },
	  params: companyId ? { companyId } : {}
	});
	return response.data;
  };
  
  /**
   * Obtém um vínculo app–company pelo ID.
   *
   * Endpoint: GET /company-apps/:id
   *
   * Headers:
   *   - Authorization: Bearer <token>
   *
   * URL Parameter:
   *   - id (number): Identificador do vínculo.
   *
   * Response:
   *   - { id, company_id, app_id, app_name, max_users, created_at }
   *
   * Errors:
   *   - 404: Registro não encontrado.
   *   - 500: Erro interno.
   */
  export const getCompanyAppById = async (token, id) => {
	const response = await api.get(`/company-apps/${id}`, {
	  headers: { Authorization: `Bearer ${token}` }
	});
	return response.data;
  };
  
  /**
   * Cria um novo vínculo app–company.
   *
   * Endpoint: POST /company-apps
   *
   * Headers:
   *   - Authorization: Bearer <token>
   *
   * Request Body:
   *   - company_id (number, obrigatório)
   *   - app_id (number, obrigatório)
   *   - max_users (number, opcional)
   *
   * Response:
   *   - { message: "company_app criado", id: number }
   *
   * Errors:
   *   - 400: Campos obrigatórios faltando.
   *   - 500: Erro interno.
   */
  export const addCompanyApp = async (token, companyApp) => {
	const response = await api.post('/company-apps', companyApp, {
	  headers: { Authorization: `Bearer ${token}` }
	});
	return response.data;
  };
  
  /**
   * Atualiza o número de usuários de um vínculo app–company.
   *
   * Endpoint: PUT /company-apps/:id
   *
   * Headers:
   *   - Authorization: Bearer <token>
   *
   * URL Parameter:
   *   - id (number): Identificador do vínculo.
   *
   * Request Body:
   *   - max_users (number)
   *
   * Response:
   *   - { message: "Atualizado com sucesso" }
   *
   * Errors:
   *   - 404: Registro não encontrado.
   *   - 500: Erro interno.
   */
  export const updateCompanyApp = async (token, id, updates) => {
	const response = await api.put(`/company-apps/${id}`, updates, {
	  headers: { Authorization: `Bearer ${token}` }
	});
	return response.data;
  };
  
  /**
   * Remove um vínculo app–company.
   *
   * Endpoint: DELETE /company-apps/:id
   *
   * Headers:
   *   - Authorization: Bearer <token>
   *
   * URL Parameter:
   *   - id (number): Identificador do vínculo.
   *
   * Response:
   *   - { message: "Deletado com sucesso" }
   *
   * Errors:
   *   - 404: Registro não encontrado.
   *   - 500: Erro interno.
   */
  export const deleteCompanyApp = async (token, id) => {
	const response = await api.delete(`/company-apps/${id}`, {
	  headers: { Authorization: `Bearer ${token}` }
	});
	return response.data;
  };
  
  /**
 * Obtém o uso de usuários por app para uma empresa específica.
 *
 * Endpoint: GET /company-apps/usage/:companyId
 *
 * Headers:
 *   - Authorization: Bearer <token>
 *
 * URL Parameter:
 *   - companyId (number): Identificador da empresa.
 *
 * Response:
 *   - Array de objetos { company_id, app_id, app_name, max_users, total_unique_users }
 *
 * Errors:
 *   - 500: Erro interno.
 */
export const getAppUserUsage = async (token, companyId) => {
	const response = await api.get(`/company-apps/usage/${companyId}`, {
	  headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
  };